import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarPlus, Settings as SettingsIcon, Sparkles } from "lucide-react";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Avatar } from "../components/ui/Avatar";
import { VisitCard } from "../components/VisitCard";
import { Wordmark } from "../components/Brand";
import { api, ApiException } from "../lib/api";

interface MeResponse {
  user: { id: string; email: string; name: string | null } | null;
  circles: Array<{
    id: string;
    name: string;
    role: "admin" | "member";
    loved_one_name: string | null;
  }>;
}

interface UpcomingVisit {
  id: string;
  title: string | null;
  provider_name_snapshot: string | null;
  provider_specialty_snapshot: string | null;
  facility_name_snapshot: string | null;
  starts_at: string;
  arrive_lead_minutes: number;
  visit_category: string | null;
  status: "open" | "accepted" | "cancelled" | "completed";
  ride_required: number;
}

export function Dashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingVisit[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<MeResponse>("/api/me");
        if (!data.user) return navigate("/signin", { replace: true });
        setMe(data);
        if (data.circles.length > 0) {
          const now = new Date().toISOString();
          const r = await api.get<{ visits: UpcomingVisit[] }>(
            `/api/circles/${data.circles[0]!.id}/visits?from=${encodeURIComponent(now)}`,
          );
          setUpcoming(r.visits.slice(0, 3));
        }
      } catch (err) {
        if (err instanceof ApiException && err.status === 401) {
          navigate("/signin", { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center p-5 bg-bg">
        <div className="text-text-muted">Loading…</div>
      </div>
    );
  }

  // First-run: no circles yet.
  if (me && me.circles.length === 0) {
    return (
      <AppShell activeTab="home">
        <div className="mx-auto max-w-xl px-5 py-12 sm:py-20">
          <Card>
            <CardBody className="text-center">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary-soft mb-4">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Welcome to Carol Care</h1>
              <p className="mt-2 text-text-muted">
                Care is easier when the whole family can see what's happening. Let's set up
                a circle for the loved one you're helping.
              </p>
              <div className="mt-6">
                <Link to="/app/create-circle">
                  <Button size="lg">Create a care circle</Button>
                </Link>
              </div>
            </CardBody>
          </Card>
        </div>
      </AppShell>
    );
  }

  const c = me?.circles[0];
  const greeting = pickGreeting();
  const firstName = me?.user?.name?.split(" ")[0] ?? null;

  return (
    <AppShell activeTab="home">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:py-10">
        {/* Greeting */}
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-sm text-text-muted">{greeting}{firstName ? `, ${firstName}` : ""}.</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight">
              How can we help today?
            </h1>
          </div>
          <Wordmark size="sm" withMark className="hidden sm:inline-flex" />
        </div>

        {/* Circle hero */}
        {c && (
          <Card className="mt-6 overflow-hidden">
            <div
              aria-hidden
              className="h-1.5 w-full"
              style={{
                background: "linear-gradient(90deg, var(--primary), #3B82F6 50%, var(--accent))",
              }}
            />
            <CardBody>
              <div className="flex items-center gap-4">
                <Avatar name={c.loved_one_name ?? c.name} size="xl" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold tracking-tight truncate">{c.name}</h2>
                  {c.loved_one_name && (
                    <p className="text-text-muted">Caring for {c.loved_one_name}</p>
                  )}
                </div>
                <Badge tone={c.role === "admin" ? "info" : "neutral"}>
                  {c.role === "admin" ? "Admin" : "Member"}
                </Badge>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/app/visits/new">
                  <Button>
                    <CalendarPlus className="h-4 w-4" />
                    {c.role === "admin" ? "New visit" : "Propose visit"}
                  </Button>
                </Link>
                <Link to="/app/visits">
                  <Button variant="secondary">Open visits</Button>
                </Link>
                {c.role === "admin" && (
                  <Link to="/app/settings">
                    <Button variant="ghost">
                      <SettingsIcon className="h-4 w-4" /> Settings
                    </Button>
                  </Link>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Upcoming */}
        <section className="mt-8">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold tracking-tight">Up next</h2>
            <Link to="/app/visits" className="text-sm text-primary hover:underline">
              See all →
            </Link>
          </div>

          <div className="mt-3 space-y-3">
            {upcoming === null && <div className="text-text-muted text-sm">Loading…</div>}
            {upcoming?.length === 0 && (
              <Card>
                <CardBody className="text-center py-10">
                  <p className="text-text-muted">No upcoming visits.</p>
                  <Link to="/app/visits/new" className="mt-3 inline-block">
                    <Button variant="secondary">{c?.role === "admin" ? "Post one now" : "Propose one"}</Button>
                  </Link>
                </CardBody>
              </Card>
            )}
            {upcoming?.map((v) => <VisitCard key={v.id} visit={v} />)}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function pickGreeting(): string {
  // Hour in CT for this dashboard's primary audience.
  const h = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago",
      hour: "numeric",
      hour12: false,
    }).format(new Date()),
  );
  if (h < 5) return "Up late";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 22) return "Good evening";
  return "Late night";
}
